import { z } from 'zod';

type UpdateRawRecord<T extends { id: string }> = Partial<T> & { id: string };

export const requiredString = z.string().min(1);
export const allowEmptyString = z.string();
export const optionalString = z.string().nullable().optional();
export const timestamp = z.number();
export const requiredNumber = z.number();
export const requiredBoolean = z.boolean();
export const optionalBoolean = z.boolean().nullable().optional();
export const createDeleteSchema = () =>
    z
        .object({
            id: requiredString,
        })
        .strip();

export const createUpdateSchemaFromAdd = <T extends z.ZodObject<any>>(
    addSchema: T,
    requiredFields: readonly string[] = ['id'],
) =>
    addSchema
        .partial()
        .required(
            requiredFields.reduce<Record<string, true>>((acc, field) => {
                acc[field] = true;
                return acc;
            }, {}),
        )
        .strip() as unknown as z.ZodType<
        UpdateRawRecord<z.infer<T> & { id: string }>
    >;

export const SYNC_UPDATE_VALIDATION_SCHEMA = {
    contacts: ['id', 'created_at', 'updated_at', 'first_name'],
    products: ['id', 'created_at', 'updated_at', 'name'],
    procedures: [
        'id',
        'name',
        'category',
        'description',
        'price',
        'available',
        'created_at',
        'updated_at',
        'user_id',
    ],
    shopping_lists: ['id', 'created_at', 'updated_at', 'name'],
    shopping_list_products: [
        'id',
        'created_at',
        'updated_at',
        'shopping_list_id',
        'product_id',
        'unit_type',
        'quantity',
        'total_quantity',
    ],
    inventory_products: ['id', 'created_at', 'updated_at', 'product_id'],
    inventory_changes: [
        'id',
        'created_at',
        'updated_at',
        'inventory_product_id',
        'quantity_change',
        'process_at',
        'user_id',
        'organisation_id',
    ],
    users: ['id', 'created_at', 'updated_at'],
    user_settings: ['id', 'created_at', 'updated_at', 'user_id', 'enabled'],
    user_answers: ['id', 'created_at', 'updated_at', 'user_id', 'answer_id'],
    onboardings: ['id', 'created_at', 'updated_at'],
    locations: [
        'id',
        'created_at',
        'updated_at',
        'name',
        'user_id',
        'place_id',
        'lat',
        'lng',
        'url',
        'utc_offset',
    ],
    horses: ['id', 'created_at', 'updated_at', 'name'],
    horse_contacts: [
        'id',
        'created_at',
        'updated_at',
        'horse_id',
        'contact_id',
    ],
    event_contacts: [
        'id',
        'created_at',
        'updated_at',
        'event_id',
        'contact_id',
    ],
    event_contact_reminders: [
        'id',
        'created_at',
        'updated_at',
        'event_contact_id',
        'time_before',
    ],
    entries: ['id', 'created_at', 'updated_at', 'logged_time', 'invoice_id'],
    entry_procedures: [
        'id',
        'entry_id',
        'procedure_id',
        'created_at',
        'updated_at',
        'user_id',
        'name',
        'quantity',
        'price',
    ],
    entry_products: [
        'id',
        'created_at',
        'updated_at',
        'entry_id',
        'product_id',
        'quantity',
    ],
    event_horses: ['id', 'created_at', 'updated_at', 'event_id', 'horse_id'],
} as const;

export const getSyncUpdateRequiredFields = (table: string): readonly string[] =>
    SYNC_UPDATE_VALIDATION_SCHEMA[
        table as keyof typeof SYNC_UPDATE_VALIDATION_SCHEMA
    ] ?? ['id'];

export const getValidationRecord = (
    rawRecord: Record<string, unknown>,
    _table: string,
    operation: 'add' | 'update' | 'delete',
) => {
    if (operation !== 'add') return rawRecord;

    const { _status, _changed, ...recordWithoutMeta } = rawRecord;

    return recordWithoutMeta;
};
